// Eskom PARCC — per-rule regulatory references (item 1: link findings to the
// SPECIFIC Eskom Procurement Policy clause, PFMA section, and applicable law).
//
// Keyed by the matching rule's `label` (labels are unique across ROUTE_RULES).
// `classifyFinding` looks these up for the winning rule and carries them onto the
// finding (persisted as JSON in `Finding.regulatoryRefs`), so the register,
// capture UIs and dashboard can surface WHICH instruments a finding implicates.
//
// PROVENANCE / STATUS — every entry is **DRAFT, pending legal sign-off**:
//   • Public instruments (PFMA, Treasury Regulations 16A, Constitution s217,
//     PPPFA + Preferential Procurement Regulations 2022, B-BBEE Act, CIDB Act,
//     PRECCA, NT SCM Instruction Notes) were confirmed against gov.za /
//     treasury.gov.za / OCPO / SAFLII / justice.gov.za sources.
//   • Eskom is a Schedule 2 public entity, so the duty provision is PFMA **s51**
//     (accounting authority), NOT s38 (which governs departments).
//   • Eskom-internal references (procedure 32-1034 and its appendices/clauses,
//     Eskom Procurement Instruction Notes, DAA, EDMS/OpenText, integrity pact,
//     DOI/COI/NDA forms, SBD4) are taken verbatim from the app's own rulebook
//     (`routes.ts`); they are NOT publicly verifiable and are tagged
//     "VERIFY — Eskom internal".
//   • Some PFMA/TR sub-paragraph letters and the deviation reporting period are
//     flagged UNCONFIRMED in the research notes — confirm before relying on them.
//   • NB: the Public Procurement Act 28 of 2024, once its regulations commence,
//     will consolidate/replace parts of this PPPFA / TR-16A framework — confirm
//     which regime is in force for the audit period under review.

/** The three reference kinds surfaced per finding (item 1's three anchors). */
export interface RuleReferences {
  /** Specific Eskom Procurement Policy / Procedure clauses (internal — verify). */
  eskomPolicy: string[];
  /** Specific PFMA sections / Treasury Regulations contravened. */
  pfma: string[];
  /** Other applicable laws / regulations (Constitution, PPPFA, B-BBEE, etc.). */
  law: string[];
}

/**
 * Rule-label → regulatory references. DRAFT — legal sign-off pending. Keyed by
 * the exact `Rule.label` string in {@link ROUTE_RULES}; keep the two in sync.
 */
export const RULE_REFERENCES: Record<string, RuleReferences> = {
  // ---- RFQ ----------------------------------------------------------------
  'RFQ value exceeds threshold / wrong route used': {
    eskomPolicy: [
      'Eskom procedure 32-1034: 8.7.1 (informal tendering / RFQ permitted only up to R1m) — VERIFY — Eskom internal',
      'Eskom DAA / Delegation of Authority (route selection per delegated authority) — VERIFY — Eskom internal',
    ],
    pfma: [
      'PFMA s51(1)(a)(iii) — accounting authority must maintain a fair, equitable, transparent, competitive and cost-effective procurement system',
      'PFMA s51(1)(b)(ii) — duty to prevent irregular and fruitless & wasteful expenditure',
      'Treasury Regulation 16A6.1 / 16A6.3 — price quotations within threshold vs competitive bidding above it (issued under PFMA s76(4)(c))',
    ],
    law: [
      'Constitution s217(1) — fair, equitable, transparent, competitive, cost-effective',
      'NT SCM Instruction Note 02 of 2021/22 — procurement thresholds & processes (>R1m must follow competitive bidding)',
    ],
  },
  'RFQ not issued via CSD or valid deviation absent': {
    eskomPolicy: [
      'Eskom procedure 32-1034: Appendix C-C4 (CSD sourcing; screenshot evidence + Procurement Manager deviation approval if CSD unavailable) — VERIFY — Eskom internal',
      'Eskom Procurement Instruction Note 03 of 2025 (RFQ to be issued via CSD platform) — VERIFY — Eskom internal',
    ],
    pfma: [
      'Treasury Regulation 16A6.4 — deviation from competitive process only when impractical; reasons recorded and approved by the accounting authority',
      'PFMA s51(1)(b)(ii) — prevent irregular expenditure',
    ],
    law: [
      'Constitution s217(1) — competitive & transparent',
      'Central Supplier Database (NT CSD) — single supplier-registration requirement for organs of state (NT SCM Instruction on CSD)',
    ],
  },
  'Award not aligned to DAA / highest scorer / validity / tax': {
    eskomPolicy: [
      'Eskom procedure 32-1034: Appendix C-C7.7 (recommend highest scorer per PPPFA) — VERIFY — Eskom internal',
      'Eskom procedure 32-1034: Appendix C-C7.8 (fair/reasonable/market-related price) — VERIFY — Eskom internal',
      'Eskom procedure 32-1034: Appendix C-C7.10 (tax compliance at award) — VERIFY — Eskom internal',
      'Eskom DAA / Delegation of Authority — VERIFY — Eskom internal',
    ],
    pfma: [
      'PFMA s50 — fiduciary duties of the accounting authority (fidelity, honesty, integrity; best interests)',
      'PFMA s51(1)(b)(ii) — prevent irregular expenditure',
      'Treasury Regulation 16A6.3(b) — bids evaluated per a system enabling award to a determinable points scorer',
    ],
    law: [
      'Constitution s217(1)–(2)',
      'PPPFA s2(1)(f) — contract awarded to the tenderer scoring the highest points (unless objective criteria justify otherwise)',
      'Preferential Procurement Regulations 2022 (80/20 & 90/10 points systems, specific goals)',
      'Tax: NT/CSD/SARS tax-compliance verification at award',
    ],
  },
  'RFQ evaluation, COI or negotiation pack missing': {
    eskomPolicy: [
      'Eskom procedure 32-1034: Appendix C-C7 (signed evaluation sheets) — VERIFY — Eskom internal',
      'Eskom procedure 32-1034: Appendix C-C7.2 (COI declaration by end-user & practitioner) — VERIFY — Eskom internal',
      'Eskom procedure 32-1034: Appendix C-C8/C9 (negotiation process / pack) — VERIFY — Eskom internal',
      'Eskom DOI / COI declaration forms — VERIFY — Eskom internal',
    ],
    pfma: [
      'PFMA s57 — responsibilities of other officials (disclose material interests; act in best interests)',
      'Treasury Regulation 16A8.3 — officials & role-players to declare interests / sign code of conduct (sub-numbering UNCONFIRMED)',
    ],
    law: [
      'Constitution s217(1) — fair & equitable',
      'PRECCA s34 — duty to report corrupt activity (where a COI conceals a conflict)',
    ],
  },
  'RFQ returnables or supplier invitation count issue': {
    eskomPolicy: [
      'Eskom procedure 32-1034: Appendix C-C3.4 (mandatory returnables: Authorisation Form; Integrity Pact & Declaration; Declaration of Shareholding & Directorship; SBD4) — VERIFY — Eskom internal',
      'Eskom procedure 32-1034: Appendix C-C4 (minimum 6 suppliers sourced via CSD — Eskom internal requirement) — VERIFY — Eskom internal',
    ],
    pfma: [
      'Treasury Regulation 16A9.1 — combating abuse of the SCM system (declarations / integrity) (sub-numbering UNCONFIRMED)',
      'Treasury Regulation 16A6.1 — sourcing of quotations',
    ],
    law: [
      'SBD4 (Standard Bidding Document 4 — Declaration of Interest) — legislatively-derived returnable',
      'Constitution s217(1) — competitive (adequate number of suppliers invited)',
    ],
  },
  // ---- LPO ----------------------------------------------------------------
  'LPO threshold, modification or forex issue': {
    eskomPolicy: [
      'Eskom procedure 32-1034: 8.5.1 (LPO permissible only up to R30k; no CPA; no forex; no modifications) — VERIFY — Eskom internal',
    ],
    pfma: [
      'PFMA s51(1)(b)(ii) — prevent irregular and fruitless & wasteful expenditure (forex / uncovered exposure)',
      'Treasury Regulation 16A6.1 — procurement within prescribed threshold values',
    ],
    law: [
      'Constitution s217(1) — cost-effective',
      'NT SCM Instruction Note — R30,000 written-quotation threshold (VAT incl.)',
    ],
  },
  'LPO quotation, hierarchy, tax or market price missing': {
    eskomPolicy: [
      'Eskom procedure 32-1034: Appendix A-A2 (supplier hierarchy) — VERIFY — Eskom internal',
      'Eskom procedure 32-1034: Appendix A-A6 (market-related price verification) — VERIFY — Eskom internal',
      'Eskom procedure 32-1034: Appendix A-A7 (tax compliance) — VERIFY — Eskom internal',
      'Eskom procedure 32-1034: Appendix A-A10.1 (justification if not highest-ranked) — VERIFY — Eskom internal',
    ],
    pfma: [
      'PFMA s51(1)(b)(ii) — prevent irregular expenditure',
      'Treasury Regulation 16A6.1 — price quotations',
      'Treasury Regulation 16A3.2(a) — SCM system must be cost-effective / value for money',
    ],
    law: [
      'Constitution s217(1) — competitive & cost-effective',
      'PPPFA s2(1)(f) + Preferential Procurement Regulations 2022 — award to highest-points tenderer',
      'Tax: NT/CSD tax-status verification requirement',
    ],
  },
  'LPO misuse / PR / EDMS / approval issue': {
    eskomPolicy: [
      'Eskom procedure 32-1034: 8.5.1 (LPO only for non-complex, once-off, non-stock goods not on an existing contract; CPA must not apply) — VERIFY — Eskom internal',
      'Eskom procedure 32-1034: Appendix A-A1 (PR / written request from end-user) — VERIFY — Eskom internal',
      'Eskom procedure 32-1034: 8.2.2 (EDMS / OpenText document upload) — VERIFY — Eskom internal',
    ],
    pfma: [
      'PFMA s51(1)(b)(ii) — prevent irregular expenditure (splitting / contract circumvention)',
      'Treasury Regulation 16A6.4 — no avoidance of the competitive process',
      'Treasury Regulation 16A9.1(d) — avoid SCM abuse (circumventing contracts / splitting requirements) (sub-numbering UNCONFIRMED)',
    ],
    law: [
      'Constitution s217(1) — competitive & transparent',
      'NT SCM Instruction Note — threshold / process discipline',
    ],
  },
  'LPO forms / supplier count housekeeping': {
    eskomPolicy: [
      'Eskom procedure 32-1034: Appendix A-A4.2 (minimum 3 suppliers via CSD — Eskom internal requirement) — VERIFY — Eskom internal',
      'Eskom procedure 32-1034: Appendix A-A3.5 (returnables: Authorisation Form, Integrity Declaration, Shareholding & Directorship, SBD4) — VERIFY — Eskom internal',
    ],
    pfma: [
      'Treasury Regulation 16A9.1 — integrity / combating abuse (declarations) (sub-numbering UNCONFIRMED)',
      'Treasury Regulation 16A6.1 — sourcing of quotations',
    ],
    law: [
      'SBD4 (Declaration of Interest) — legislatively-derived returnable',
      'Constitution s217(1) — competitive (adequate suppliers)',
    ],
  },
  // ---- Tender -------------------------------------------------------------
  'Tender strategy / DAA / evaluation / award breach': {
    eskomPolicy: [
      'Eskom procedure 32-1034: 8.8.3 (strategy approved by relevant DAA) — VERIFY — Eskom internal',
      'Eskom procedure 32-1034: 8.8.12 (signed evaluation disciplines) — VERIFY — Eskom internal',
      'Eskom procedure 32-1034: 8.13 / 8.8.14 (adjudication & award) — VERIFY — Eskom internal',
      'Eskom procedure 32-1034: 10.2.1 & 8.8.12 (statutory docs: Tax, B-BBEE, CIDB, CSD) — VERIFY — Eskom internal',
      'Eskom DAA / Delegation of Authority — VERIFY — Eskom internal',
    ],
    pfma: [
      'PFMA s50 — fiduciary duties of the accounting authority',
      'PFMA s51(1)(a)(iii) — fair/equitable/transparent/competitive/cost-effective system',
      'PFMA s51(1)(b)(ii) — prevent irregular expenditure',
      'Treasury Regulation 16A6.3 — competitive bidding requirements & evaluation',
      'Treasury Regulation 16A9 — combating abuse of SCM',
    ],
    law: [
      'Constitution s217(1)–(3)',
      'PPPFA s2(1) + Preferential Procurement Regulations 2022 (points system; award to highest scorer)',
      'B-BBEE Act 53 of 2003 s10 — organ of state must apply the Codes in preferential procurement',
      'CIDB Act 38 of 2000 s18 — contractor must be CIDB-registered for public-sector construction works (construction tenders)',
      'NT eTender Portal / NT SCM Instruction Note — competitive-bidding obligations',
      'Tax: NT/CSD tax-compliance verification',
    ],
  },
  'Tender publications, clarification or cancellation non-compliance': {
    eskomPolicy: [
      'Eskom procedure 32-1034: 8.8.6 (advertisement to NT eTender, Eskom Bulletin, CIDB where applicable) — VERIFY — Eskom internal',
      'Eskom procedure 32-1034: 8.8.6 & 8.8.7 (clarification queries to all bidders) — VERIFY — Eskom internal',
      'Eskom procedure 32-1034: 8.8.8 & 8.8.11 (cancellation approvals; validity) — VERIFY — Eskom internal',
      'Eskom procedure 32-1034: 8.8.14(c) (publication of successful/unsuccessful on NT eTender, Eskom & CIDB portals) — VERIFY — Eskom internal',
    ],
    pfma: [
      'Treasury Regulation 16A6.3(c) — invitation of competitive bids through advertisement',
      'PFMA s51(1)(b)(ii) — prevent irregular expenditure',
    ],
    law: [
      'Constitution s217(1) — transparent, competitive, equitable (equal treatment of bidders)',
      'NT SCM Instruction Note — competitive bids >R1m advertised on Government Tender Bulletin & eTender Portal; awards published',
      'CIDB Act 38 of 2000 s22 — Register of Projects (construction tenders) (section number UNCONFIRMED)',
    ],
  },
  'Tender plan, appointments, COI/NDA, negotiation pack or EDMS issue': {
    eskomPolicy: [
      'Eskom procedure 32-1034: 8.8 / 8.8.1 (CfT formally appointed in writing) — VERIFY — Eskom internal',
      'Eskom procedure 32-1034: 4.2.1 & 4.3.3 (DOI & NDA conflict declarations) — VERIFY — Eskom internal',
      'Eskom procedure 32-1034: 8.8.12(a) (signed COI & NDA by evaluators) — VERIFY — Eskom internal',
      'Eskom procedure 32-1034: 8.8.12(q) (negotiation pack: strategy, agenda, signed minutes, DOI) — VERIFY — Eskom internal',
      'Eskom procedure 32-1034: 8.2.2 (EDMS) — VERIFY — Eskom internal',
      'Eskom Instruction Note 01 of 2024: 2.1.3 (transaction in approved divisional procurement plan) — VERIFY — Eskom internal',
    ],
    pfma: [
      'PFMA s57 — responsibilities of officials (disclose interests)',
      'Treasury Regulation 16A8.3 — declaration of interests by SCM role-players (sub-numbering UNCONFIRMED)',
      'Treasury Regulation 16A3.2 — demand management / procurement-planning element of the SCM system',
    ],
    law: [
      'Constitution s217(1) — fair, equitable, transparent',
      'PRECCA s34 — duty to report (undeclared conflicts / corrupt activity)',
    ],
  },
  'Tender administrative filing issue': {
    eskomPolicy: [
      'Eskom procedure 32-1034: 8.2.2 (documents on EDMS; alignment of physical file & EDMS) — VERIFY — Eskom internal',
      'Eskom EDMS / OpenText document-management standard — VERIFY — Eskom internal',
    ],
    pfma: [
      'Treasury Regulation 16A — SCM record-keeping / audit trail element of the financial-management system',
      'PFMA s51(1)(e)(iii) — proper records / management-information system (sub-paragraph UNCONFIRMED)',
    ],
    law: [
      'Constitution s217(1) — transparent (auditable record)',
      'Housekeeping — no specific external statutory breach beyond record-keeping',
    ],
  },
  // ---- Emergency ----------------------------------------------------------
  'Emergency did not qualify / urgency not demonstrated / reporting issue': {
    eskomPolicy: [
      'Eskom procedure 32-1034: 8.9.4(a) (emergency criteria: immediate risk; urgent action; insufficient time for competitive tenders) — VERIFY — Eskom internal',
      'Eskom procedure 32-1034: 8.9.4(c) (quotation requested only after emergency declared/approved) — VERIFY — Eskom internal',
      'Eskom Procurement Instruction 09 of 2024 (3.26) (report to NT & AGSA within a month via OCPO) — VERIFY — Eskom internal',
    ],
    pfma: [
      'Treasury Regulation 16A6.4 — deviation only when impractical to invite competitive bids; reasons recorded & approved',
      'PFMA s51(1)(b)(ii) — prevent irregular expenditure (deviation abuse)',
      'Treasury Regulation 16A6.4 (reporting limb) — report deviations to the relevant treasury & Auditor-General (period/threshold per NT instruction — UNCONFIRMED)',
    ],
    law: [
      'Constitution s217(1) — competitive (deviation must be genuinely justified)',
      'NT SCM Instruction Note on deviations & expansions — reporting to OCPO / National Treasury',
      'AGSA reporting (Public Audit Act framework) and OCPO reporting',
    ],
  },
  'Emergency award / DAA / tax / BBBEE / CSD issue': {
    eskomPolicy: [
      'Eskom procedure 32-1034: 8.9.4(c) (DAA ratification of emergency submission report) — VERIFY — Eskom internal',
      'Eskom procedure 32-1034: 10.2.1 (valid B-BBEE certificate / sworn affidavit) — VERIFY — Eskom internal',
      'Eskom procedure 32-1034: 8.8.4(k) & 4.2.7 (SBD4 & integrity pact) — VERIFY — Eskom internal',
      'Eskom procedure 32-1034: 8.8.12(p) (tax compliance — CSD report / SARS e-filing) — VERIFY — Eskom internal',
      'Eskom DAA / Delegation of Authority — VERIFY — Eskom internal',
    ],
    pfma: [
      'PFMA s50 — fiduciary duties (ratification by accounting authority / DAA)',
      'PFMA s51(1)(b)(ii) — prevent irregular expenditure',
    ],
    law: [
      'Constitution s217(1)–(2)',
      'B-BBEE Act 53 of 2003 s10 — apply the B-BBEE Codes',
      'PPPFA s2 + Preferential Procurement Regulations 2022 (preference still applies on emergency awards)',
      'Central Supplier Database (CSD) — supplier registration / tax verification (NT CSD requirement)',
      'SBD4 (Declaration of Interest) — legislatively-derived returnable',
      'Tax: NT/CSD tax-compliance verification',
    ],
  },
  'Emergency request form / mitigation / SAP / EDMS issue': {
    eskomPolicy: [
      'Eskom procedure 32-1034: 8.9.4(b)(c) (approved Emergency Request Form by DE/GE/MD/GM ERI) — VERIFY — Eskom internal',
      'Eskom procedure 32-1034: 8.9.4(c) (mitigation actions; not within existing contract; ZEM PR on SAP; supporting docs on PR) — VERIFY — Eskom internal',
      'Eskom procedure 32-1034: 8.2.2 (EDMS upload) — VERIFY — Eskom internal',
    ],
    pfma: [
      'Treasury Regulation 16A6.4 — record & approve reasons for deviation',
      'PFMA s51(1)(e)(iii) — proper records & management-information system (sub-paragraph UNCONFIRMED)',
    ],
    law: [
      'Constitution s217(1) — transparent (auditable justification)',
      'Largely Eskom-internal process & record-keeping; external anchor is the TR 16A6.4 deviation-record duty',
    ],
  },
  // ---- Urgent -------------------------------------------------------------
  'Urgent procurement did not qualify / planning failure / bidding possible': {
    eskomPolicy: [
      'Eskom Procurement Instruction 2 of 2025 (a) (urgent criteria: early delivery critical; impractical to invite competitive bids; not due to lack of planning; qualifying circumstances) — VERIFY — Eskom internal',
      'Eskom Procurement Instruction 2 of 2025 (c) (quotation requested only after Urgent Procurement Request Form approved) — VERIFY — Eskom internal',
    ],
    pfma: [
      'Treasury Regulation 16A6.4 — deviation only when impractical to invite competitive bids; reasons recorded & approved (lack of planning is not a valid ground)',
      'PFMA s51(1)(b)(ii) — prevent irregular expenditure',
      'PFMA s51(1)(a)(iii) — competitive system',
    ],
    law: [
      'Constitution s217(1) — competitive (foreseeable need must be competitively procured)',
      'NT SCM Instruction Note on deviations — AGSA / OCPO reporting',
    ],
  },
  'Urgent award / DAA / COI / BBBEE / CSD / tax issue': {
    eskomPolicy: [
      'Eskom Procurement Instruction 2 of 2025 (c) (DAA approval of ratification report; segregation from supporting Senior Manager; COI declarations; negotiation pack) — VERIFY — Eskom internal',
      'Eskom procedure 32-1034: 10.2.1 (B-BBEE certificate / affidavit) — VERIFY — Eskom internal',
      'Eskom procedure 32-1034: 8.9.5(b) (CSD report) — VERIFY — Eskom internal',
      'Eskom procedure 32-1034: 8.8.4(k) (SBD4) — VERIFY — Eskom internal',
      'Eskom procedure 32-1034: 8.8.12(p) (tax compliance — CSD / SARS) — VERIFY — Eskom internal',
    ],
    pfma: [
      'PFMA s50 — fiduciary duties (DAA approval)',
      "PFMA s57 — official's duty to declare interests (COI)",
      'PFMA s51(1)(b)(ii) — prevent irregular expenditure',
    ],
    law: [
      'Constitution s217(1)–(2)',
      'B-BBEE Act 53 of 2003 s10',
      'PPPFA s2 + Preferential Procurement Regulations 2022',
      'Central Supplier Database (CSD) — NT registration / tax verification',
      'PRECCA s34 — duty to report (undeclared COI / corruption)',
      'SBD4 (Declaration of Interest) — legislatively-derived returnable',
    ],
  },
  'Urgent request form / contract duration / PR / EDMS issue': {
    eskomPolicy: [
      'Eskom Procurement Instruction 2 of 2025 (a) (Urgent Procurement Request Form; max 12-month contract period; steps to prevent future reliance) — VERIFY — Eskom internal',
      'Eskom Procurement Instruction 2 of 2025 (c) (not within existing contract; supporting docs on PR; modification controls) — VERIFY — Eskom internal',
      'Eskom procedure 32-1034: 8.2.2 (EDMS upload) — VERIFY — Eskom internal',
    ],
    pfma: [
      'Treasury Regulation 16A6.4 — record & approve reasons for deviation',
      'PFMA s51(1)(e)(iii) — proper records / management-information system (sub-paragraph UNCONFIRMED)',
    ],
    law: [
      'Constitution s217(1) — transparent (auditable record; bounded duration prevents circumvention)',
      'Largely Eskom-internal process & record-keeping; external anchor is the TR 16A6.4 deviation-record duty',
    ],
  },
  // ---- Generic ------------------------------------------------------------
  'Generic possible irregular expenditure trigger': {
    eskomPolicy: [
      'Eskom procedure 32-1034 (overarching procurement & SCM procedure) — VERIFY — Eskom internal',
      'Eskom DAA / Delegation of Authority — VERIFY — Eskom internal',
    ],
    pfma: [
      "PFMA s1 — definitions of 'irregular expenditure' and 'fruitless and wasteful expenditure'",
      'PFMA s51(1)(b)(ii) — accounting authority duty to prevent irregular and fruitless & wasteful expenditure',
      'PFMA s55(2)(b)(i) — disclosure of irregular / fruitless & wasteful expenditure in the annual financial statements (sub-paragraph UNCONFIRMED)',
      'Treasury Regulation 16A — SCM framework (issued under PFMA s76(4)(c))',
    ],
    law: [
      'Constitution s217(1) — fair, equitable, transparent, competitive, cost-effective',
      'PPPFA + Preferential Procurement Regulations 2022 (where preference mis-applied)',
      'Tax: NT/CSD tax-compliance verification',
      'NT SCM Instruction Notes (irregular-expenditure framework / guideline)',
    ],
  },
  'Generic policy and procedure control gap': {
    eskomPolicy: [
      'Eskom procedure 32-1034 (procurement plan, CfT appointment, COI/DOI/NDA, evaluation, adjudication, negotiation, DAA) — VERIFY — Eskom internal',
      'Eskom DOI / COI / NDA declaration forms — VERIFY — Eskom internal',
    ],
    pfma: [
      'PFMA s50 — fiduciary duties of the accounting authority',
      'PFMA s51(1)(a)(iii) — maintain a fair/equitable/transparent/competitive/cost-effective procurement system',
      'PFMA s57 — officials’ duties (disclose interests; act in best interests)',
      'Treasury Regulation 16A8.3 — declaration of interests; 16A3.2 — SCM system elements (sub-numbering UNCONFIRMED)',
    ],
    law: [
      'Constitution s217(1) — fair, equitable, transparent',
      'PRECCA s34 — duty to report (undeclared conflicts / corruption)',
    ],
  },
  'Generic administrative or filing gap': {
    eskomPolicy: [
      'Eskom procedure 32-1034: 8.2.2 (EDMS / OpenText document management; physical-file alignment) — VERIFY — Eskom internal',
      'Eskom EDMS / OpenText standard — VERIFY — Eskom internal',
    ],
    pfma: [
      'PFMA s51(1)(e)(iii) — keep full and proper records; management-information system (sub-paragraph UNCONFIRMED)',
      'Treasury Regulation 16A — SCM record-keeping / audit trail',
    ],
    law: [
      'Constitution s217(1) — transparent (auditable record)',
      'Housekeeping — no specific external statutory breach beyond record-keeping',
    ],
  },
  // ---- Statutory documents (Jun 11 2026 relaxation) -----------------------
  'Statutory documents not provided / verified': {
    eskomPolicy: [
      'Eskom procedure 32-1034: 10.2.1 & 8.8.12 (statutory documents at award: Tax, B-BBEE, CIDB, CSD) — VERIFY — Eskom internal',
      'Eskom procedure 32-1034: Appendix C-C7.10 / A-A7 (tax-compliance verification at award) — VERIFY — Eskom internal',
      'Eskom procedure 32-1034: 8.8.4(k) & 4.2.7 (SBD4 / integrity pact returnables) — VERIFY — Eskom internal',
    ],
    pfma: [
      'PFMA s51(1)(a)(iii) — fair, equitable, transparent, competitive, cost-effective system (supplier eligibility verified)',
      'PFMA s51(1)(b)(ii) — prevent irregular expenditure (award to a statutorily non-compliant supplier)',
      'Treasury Regulation 16A9 — combating abuse of SCM (supplier eligibility / declarations)',
    ],
    law: [
      'Constitution s217(1) — fair, equitable, transparent, competitive, cost-effective',
      'B-BBEE Act 53 of 2003 s10 — organ of state must apply the B-BBEE Codes (valid B-BBEE certificate / sworn affidavit)',
      'CIDB Act 38 of 2000 s18 — CIDB registration for public-sector construction works',
      'Tax Administration Act 28 of 2011 — SARS tax-compliance status (NT / CSD verification at award)',
      'Companies Act 71 of 2008 — CIPC company registration',
      'Central Supplier Database (NT CSD) — mandatory supplier registration for organs of state',
      'SBD4 (Standard Bidding Document 4 — Declaration of Interest) — legislatively-derived returnable',
    ],
  },
};

/**
 * Look up the regulatory references for a rule by its label. Returns undefined
 * when the rule has no mapped references (so the classifier omits the field
 * rather than emitting an empty object).
 */
export function referencesForRule(label: string | undefined): RuleReferences | undefined {
  if (!label) return undefined;
  return RULE_REFERENCES[label];
}
